import { useEffect } from 'react';
import { ClipStore } from '../types';

interface UseTransitionMonitoringParams {
  store: ClipStore;
  currentClipIndex: number;
  activeVideo: 'video1' | 'video2';
  video1Ref: React.RefObject<HTMLVideoElement | null>;
  video2Ref: React.RefObject<HTMLVideoElement | null>;
  preloadingVideoRef: React.RefObject<'video1' | 'video2' | null>;
  handleClipTransition: (nextClipIndex: number) => void;
}

export const useTransitionMonitoring = ({
  store,
  currentClipIndex,
  activeVideo,
  video1Ref,
  video2Ref,
  preloadingVideoRef,
  handleClipTransition,
}: UseTransitionMonitoringParams) => {
  useEffect(() => {
    if (!store.isPlaying || store.clips.length === 0) {
      return;
    }

    let animationFrameId: number;

    const monitorTransitions = () => {
      const activeVideoRef = activeVideo === 'video1' ? video1Ref : video2Ref;
      const video = activeVideoRef.current;
      const currentClip = store.clips[currentClipIndex];

      if (video && currentClip && video.readyState >= 2) {
        const timeUntilEnd = currentClip.endTime - video.currentTime;

        // Check for transition condition more frequently
        if (timeUntilEnd <= 0.05) {
          // 50ms threshold for immediate switch
          video.pause();
          delete video.dataset.overlapTriggered;

          if (currentClipIndex >= store.clips.length - 1) {
            store.setIsPlaying(false);
            store.setCurrentTime(store.totalClipsDuration);
            return;
          }

          if (preloadingVideoRef.current) {
            const nextClipIndex = currentClipIndex + 1;
            handleClipTransition(nextClipIndex);
            return;
          }
        }
      }

      animationFrameId = requestAnimationFrame(monitorTransitions);
    };

    animationFrameId = requestAnimationFrame(monitorTransitions);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [
    store.isPlaying,
    store.clips,
    currentClipIndex,
    activeVideo,
    store.totalClipsDuration,
    preloadingVideoRef,
    handleClipTransition,
  ]);
};
